import { useEffect, useRef, useState } from "react";
import { PlayCircle, CalendarDays, Smartphone, MessageSquare, Dumbbell, CreditCard, Zap } from "lucide-react";

const TOUR_ID = "b-xylphfnfI";
const CHAPTERS = [
  ["Scheduling", CalendarDays, 0], ["Client portal", Smartphone, 34], ["Messaging", MessageSquare, 69],
  ["Programs & library", Dumbbell, 104], ["Payments", CreditCard, 139], ["Automations", Zap, 174],
];
const PLAYER_VARS = { rel: 0, modestbranding: 1, playsinline: 1 };

const loadYT = () => new Promise((res) => {
  if (window.YT?.Player) return res(window.YT);
  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => { prev?.(); res(window.YT); };
  if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) { const s = document.createElement("script"); s.src = "https://www.youtube.com/iframe_api"; document.head.appendChild(s); }
});

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export default function PlatformTour() {
  const host = useRef(null);
  const player = useRef(null);
  const [ready, setReady] = useState(false);
  const [time, setTime] = useState(0);
  useEffect(() => {
    let dead = false, tick;
    loadYT().then((YT) => {
      if (dead || !host.current) return;
      player.current = new YT.Player(host.current, {
        videoId: TOUR_ID, playerVars: PLAYER_VARS,
        events: {
          onReady: () => setReady(true),
          onStateChange: (e) => {
            clearInterval(tick);
            if (e.data === YT.PlayerState.PLAYING) tick = setInterval(() => setTime(e.target.getCurrentTime()), 500);
          },
        },
      });
    });
    return () => { dead = true; clearInterval(tick); try { player.current?.destroy(); } catch { /* noop */ } };
  }, []);
  const starts = CHAPTERS.map(([, , at]) => at);
  const active = starts.reduce((acc, s, i) => (time >= s ? i : acc), 0);
  const jump = (i) => {
    const p = player.current; if (!p?.seekTo) return;
    const st = p.getPlayerState?.();
    if (st === -1 || st === 5) p.loadVideoById({ videoId: TOUR_ID, startSeconds: starts[i] }); else { p.seekTo(starts[i], true); p.playVideo(); }
    setTime(starts[i]);
  };
  return (
    <div className="tour fade" data-testid="admin-platform-tour">
      <div className="tour-head">
        <span className="tour-kick"><PlayCircle size={14} strokeWidth={2.5} /> Platform tour</span>
        <h2 data-testid="tour-title">See everything SpottrOS can do</h2>
        <p className="ss">A guided walkthrough of scheduling, client portals, programs, payments and automations.</p>
      </div>
      <div className="tour-player" data-testid="tour-player">
        <div ref={host} data-testid="tour-iframe" />
      </div>
      <div className="tour-chapters" data-testid="tour-chapters">
        {CHAPTERS.map(([label, Icon], i) => (
          <button type="button" key={label} className={`tour-chapter ${i === active && time > 0 ? "on" : ""}`} onClick={() => jump(i)} disabled={!ready} data-testid={`tour-chapter-${i}`}>
            <span className="ic"><Icon size={16} strokeWidth={2.2} /></span>
            <span className="tx"><b>{label}</b><small className="num">{mmss(starts[i])}</small></span>
          </button>
        ))}
      </div>
    </div>
  );
}
